/**
 * 素材库管理器
 */

interface CmpsCache {
  [materialId: string]: {
    [cmpType: string]: Cmp.ConfigModel;
  };
}

interface EventsCache {
  [materialId: string]: {
    [eventType: string]: Event.ConfigModel;
  };
}

interface SettingsCache {
  [materialId: string]: {
    [type: string]: Setting.ConfigModel;
  };
}

interface ActivitiesCache {
  [materialId: string]: {
    [type: string]: Activity.ConfigModel;
  };
}

interface DataSourceCache {
  [materialId: string]: {
    [dataSourceKey: string]: DataSource.ConfigModel;
  };
}

export default class MaterialsManager {
  materialsCache: Record<string, Material.MaterialModel> = {};
  cmpsCache: CmpsCache = {};
  eventsCache: EventsCache = {};
  settingCache: SettingsCache = {};
  activitiesCache: ActivitiesCache = {};
  dataSourceCache: DataSourceCache = {};

  constructor(materials?: Record<string, Material.MaterialModel>) {
    if (materials) {
      this.addMaterials(materials);
    }
  }

  addMaterials(materials: Record<string, Material.MaterialModel>) {
    this.materialsCache = {
      ...this.materialsCache,
      ...materials,
    };
    this.initCmpsCache();
    this.initEventsCache();
    this.initSettingsCache();
    this.initActivitiesCache();
    this.initDataSourceCache();
  }

  /**
   * 初始化cmps缓存数据
   */
  initCmpsCache() {
    const cmpsCache: CmpsCache = {};
    for (const key in this.materialsCache) {
      const material = this.materialsCache[key];
      cmpsCache[key] = {};
      for (const cmpKey in material.cmps) {
        const cmp = material.cmps[cmpKey];
        if (cmp) {
          cmpsCache[key][cmp.type] = cmp;
        }
      }
    }
    this.cmpsCache = cmpsCache;
  }

  /**
   * 初始化events缓存数据
   */
  initEventsCache() {
    const eventsCache: EventsCache = {};
    for (const key in this.materialsCache) {
      const material = this.materialsCache[key];
      eventsCache[key] = {};
      if (material.events) {
        for (const eventKey in material.events) {
          const event = material.events[eventKey];
          eventsCache[key][event.type] = event;
        }
      }
    }
    this.eventsCache = eventsCache;
  }

  /**
   * 初始化 settings 缓存数据
   */
  initSettingsCache() {
    const settingsCache: SettingsCache = {};
    for (const key in this.materialsCache) {
      const material = this.materialsCache[key];
      settingsCache[key] = {};
      for (const settingKey in material.settings) {
        const setting = material.settings[settingKey];
        settingsCache[key][setting.type] = setting;
      }
    }
    this.settingCache = settingsCache;
  }

  /**
   * 初始化 dataSource 缓存数据
   */
  initDataSourceCache() {
    const dataSourceCache: DataSourceCache = {};
    for (const key in this.materialsCache) {
      const material = this.materialsCache[key];
      dataSourceCache[key] = {};

      for (const dataSourceKey in material.dataSources) {
        const dataSource = material.dataSources[dataSourceKey];
        dataSourceCache[key][dataSource.dataSourceInfo.key] = dataSource;
      }
    }

    this.dataSourceCache = dataSourceCache;
  }

  /**
   * 初始化 activities 缓存数据
   */
  initActivitiesCache() {
    const activitiesCache: ActivitiesCache = {};
    for (const key in this.materialsCache) {
      const material = this.materialsCache[key];
      activitiesCache[key] = {};
      for (const activityKey in material.activities) {
        const setting = material.activities[activityKey];
        activitiesCache[key][setting.type] = setting;
      }
    }
    this.activitiesCache = activitiesCache;
  }

  /**
   * 根据id获取素材模块
   * @param id
   */
  getMaterialById(id: string) {
    return this.materialsCache[id];
  }

  /**
   * 获取单个素材组件配置
   * @param materialId
   * @param cmpType
   */
  getCmpConfig(materialId: string, cmpType: string) {
    return this.cmpsCache[materialId] && this.cmpsCache[materialId][cmpType];
  }

  /**
   * 获取单个事件配置
   * @param materialId
   * @param eventType
   */
  getEventConfig(materialId: string, eventType: string) {
    return this.eventsCache[materialId] && this.eventsCache[materialId][eventType];
  }

  /**
   * 获取单个全局设置配置
   * @param materialId
   * @param cmpType
   */
  getSettingConfig(materialId: string, settingType: string) {
    return this.settingCache[materialId] && this.settingCache[materialId][settingType];
  }

  /**
   * 获取单个活动素材配置
   * @param materialId
   * @param activityType
   */
  getActivityConfig(materialId: string, activityType: string) {
    return this.activitiesCache[materialId] && this.activitiesCache[materialId][activityType];
  }

  /**
   * 获取单个数据源素材配置
   * @param materialId
   * @param dataSourceKey
   */
  getDataSouceConfig(materialId: string, dataSourceKey: string) {
    return this.dataSourceCache[materialId] && this.dataSourceCache[materialId][dataSourceKey];
  }
}
